import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaChevronLeft, FaChevronRight, FaSearchPlus } from "react-icons/fa";
// import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth/mammoth.browser";
import ExcelJS from "exceljs";
import { getFileIcon, getFileTypeFromName } from "../../atoms/FileTypeUtils/fileTypeUtils";
import Loader from "../../atoms/Loader";

// Set up PDF.js worker
// pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
export interface PreviewFile {
  url: string;
  name: string;
  type?: string;
}

interface FilePreviewModalProps {
  isOpen: boolean;
  files: PreviewFile[];
  initialIndex?: number;
  onClose: () => void;
}

/**
 * Get MIME type from file name based on extension
 */
const getFileTypeFromExtension = (fileName: string): string => {
  if (!fileName) return "";
  const extension = fileName.split(".").pop()?.toLowerCase() || "";

  const typeMap: Record<string, string> = {
    // Images
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    bmp: "image/bmp",
    webp: "image/webp",
    svg: "image/svg+xml",
    heic: "image/heic",

    // Documents
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    txt: "text/plain",

    // Spreadsheets
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    csv: "text/csv",

    // Audio
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
    m4a: "audio/mp4",
    webm: "audio/webm",

    // Archives
    zip: "application/zip",
    rar: "application/x-rar-compressed",
    "7z": "application/x-7z-compressed",
  };

  return typeMap[extension] || "application/octet-stream";
};

/**
 * The Office / Google viewers fetch the file from their own servers, so they can
 * only read publicly reachable http(s) URLs. A file that only exists locally (a
 * just-picked upload, or an upload whose server URL never came back) has a
 * `blob:`/`data:` URL - handing that to the viewer renders a 404, so those are
 * previewed in-browser instead.
 * Only the `blob:` *scheme* counts here: a hosted link that merely contains the
 * word "blob" (e.g. *.blob.core.windows.net) is a normal remote URL.
 */
const isLocalUrl = (url?: string): boolean => {
  if (!url) return true;
  return /^(blob:|data:)/i.test(url) || !/^https?:\/\//i.test(url);
};

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  files,
  initialIndex = 0,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const [textContent, setTextContent] = useState("");
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZoomExpanded, setIsZoomExpanded] = useState(false);

  // PDF state
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pdfPageNum, setPdfPageNum] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfScale, setPdfScale] = useState(1);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const zoomIn = useCallback(() => {
  setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
}, []);

const zoomOut = useCallback(() => {
  const newZoom = Math.max(zoomLevel - 0.1, 0.5);
  setZoomLevel(newZoom);
  if (Math.round(newZoom * 100) === 100) {
    setTimeout(() => setIsZoomExpanded(false), 300);
  }
}, [zoomLevel]);

  // const zoomIn = useCallback(() => {
  //   if (previewFile?.type === "application/pdf") {
  //     setPdfScale((prev) => Math.min(prev + 0.1, 3));
  //   } else {
  //     setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
  //   }
  // }, [previewFile?.type]);

  // const zoomOut = useCallback(() => {
  //   if (previewFile?.type === "application/pdf") {
  //     const newScale = Math.max(pdfScale - 0.1, 0.5);
  //     setPdfScale(newScale);
  //     if (Math.round(newScale * 100) === 100) {
  //       setTimeout(() => setIsZoomExpanded(false), 300);
  //     }
  //   } else {
  //     const newZoom = Math.max(zoomLevel - 0.1, 0.5);
  //     setZoomLevel(newZoom);
  //     if (Math.round(newZoom * 100) === 100) {
  //       setTimeout(() => setIsZoomExpanded(false), 300);
  //     }
  //   }
  // }, [previewFile?.type, pdfScale, zoomLevel]);

  // Lock body scroll when modal opens
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    // Don't set overflow when closing - let the parent ChatDrawer handle it

    return () => {
      if (isOpen) {
        // Only restore if we were the one who set it to hidden
        document.body.style.overflow = "auto";
      }
    };
  }, [isOpen]);

  // Helper: render an ExcelJS worksheet as a safe HTML table (replacement for
// the previous `XLSX.utils.sheet_to_html` call which came from the vulnerable
// `xlsx` package). Cell values are HTML-escaped to prevent XSS.
const escapeHtml = (str: string): string =>
  String(str).replace(/[&<>"']/g, (m) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[m] as string));

const cellToString = (val: unknown): string => {
  if (val === null || val === undefined) return "";
  if (val instanceof Date) return val.toISOString();
  if (typeof val === "object") {
    const obj = val as { text?: string; result?: unknown; richText?: { text: string }[] };
    if (obj.richText && Array.isArray(obj.richText)) {
      return obj.richText.map((r) => r.text || "").join("");
    }
    if (obj.text !== undefined) return String(obj.text);
    if (obj.result !== undefined) return String(obj.result);
    return JSON.stringify(val);
  }
  return String(val);
};

const worksheetToHtml = (worksheet: ExcelJS.Worksheet): string => {
  let html =
    '<table style="border-collapse: collapse; min-width: max-content;">';
  worksheet.eachRow({ includeEmpty: true }, (row) => {
    html += "<tr>";
    row.eachCell({ includeEmpty: true }, (cell) => {
      html += `<td style="border: 1px solid #ddd; padding: 4px;">${escapeHtml(cellToString(cell.value))}</td>`;
    });
    html += "</tr>";
  });
  html += "</table>";
  return html;
};

  // Update current file when index changes
  useEffect(() => {
    if (isOpen && files && files.length > 0 && currentIndex < files.length) {
      loadFilePreview(files[currentIndex]);
    }
  }, [isOpen, currentIndex, files]);

  // Reset to initial index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  // Render PDF page when doc/page/scale changes
  // useEffect(() => {
  //   if (!pdfDoc || !canvasRef.current || previewFile?.type !== "application/pdf") return;

  //   const renderPage = async () => {
  //     try {
  //       const page = await pdfDoc.getPage(pdfPageNum);
  //       const canvas = canvasRef.current;
  //       if (!canvas) return;
        
  //       const context = canvas.getContext("2d");
  //       if (!context) return;

  //       const viewport = page.getViewport({ scale: pdfScale });

  //       canvas.width = viewport.width;
  //       canvas.height = viewport.height;

  //       const renderContext = {
  //         canvasContext: context,
  //         viewport: viewport,
  //       };

  //       await page.render(renderContext).promise;
  //     } catch (error) {
  //       console.error("Error rendering PDF page:", error);
  //     }
  //   };

  //   renderPage();
  // }, [pdfDoc, pdfPageNum, pdfScale, previewFile]);

  const loadFilePreview = async (file: PreviewFile) => {
    // Infer type from filename if not provided
    const inferredType = file.type || getFileTypeFromExtension(file.name);
    const fileWithType = { ...file, type: inferredType };
    setPreviewFile(fileWithType);
    setIsLoadingPreview(true);
    setTextContent("");
    setPdfDoc(null);
    setPdfPageNum(1);
    setPdfNumPages(0);
    setPdfScale(1);
    setZoomLevel(1);

    try {
      if (!file.url) {
        console.error("No URL provided for file");
        setIsLoadingPreview(false);
        return;
      }

      // For images, just set loading to false (they load via img tag)
      if (inferredType.startsWith("image/")) {
        return;
      }

      // Handle text files
      if (inferredType === "text/plain") {
        const response = await fetch(file.url);
        const text = await response.text();
        setTextContent(text);
        setIsLoadingPreview(false);
      }
      // Handle PDF files
      else if (inferredType === "application/pdf" || file.name?.endsWith(".pdf")) {
        setIsLoadingPreview(false);
        // try {
        //   setPdfScale(1);
        //   const loadingTask = pdfjsLib.getDocument(file.url);
        //   const pdf = await loadingTask.promise;
        //   setPdfDoc(pdf);
        //   setPdfNumPages(pdf.numPages);
        //   setPdfPageNum(1);
        //   setIsLoadingPreview(false);
        // } catch (error) {
        //   console.error("Error loading PDF:", error);
        //   setIsLoadingPreview(false);
        // }
      }
      // Handle DOCX files
      else if (
        inferredType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.name?.endsWith(".docx") ||
        file.name?.endsWith(".doc")
      ) {
        const response = await fetch(file.url);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setTextContent(result.value);
        setIsLoadingPreview(false);
      }
      // Handle Excel files
      else if (
        inferredType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        inferredType === "application/vnd.ms-excel" ||
        file.name?.endsWith(".xlsx") ||
        file.name?.endsWith(".xls")
      ) {
        const response = await fetch(file.url);
        const arrayBuffer = await response.arrayBuffer();
        // Security: migrated from `xlsx` to `exceljs` (XSS-safe, no known CVEs).
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);
        const firstSheet = workbook.worksheets[0];
        const htmlContent = firstSheet ? worksheetToHtml(firstSheet) : "";
        setTextContent(htmlContent);
        setIsLoadingPreview(false);
      }
      // Handle CSV files
      else if (inferredType === "text/csv" || file.name?.endsWith(".csv")) {
        const response = await fetch(file.url);
        const text = await response.text();
        const rows = text.split("\n").map((row) => row.split(","));
        const htmlTable = `
          <table style="border-collapse: collapse; min-width: max-content;">
            ${rows
              .map(
                (row, i) => `
              <tr style="border-bottom: 1px solid #ddd;">
                ${row
                  .map(
                    (cell) => `
                  <td style="padding: 8px; border: 1px solid #ddd; white-space: nowrap; ${i === 0 ? "font-weight: bold; background-color: #f3f4f6;" : ""}">
                    ${cell.trim()}
                  </td>
                `
                  )
                  .join("")}
              </tr>
            `
              )
              .join("")}
          </table>
        `;
        setTextContent(htmlTable);
        setIsLoadingPreview(false);
      } else {
        setIsLoadingPreview(false);
      }
    } catch (error) {
      console.error("Error loading preview:", error);
      setTextContent("");
      setIsLoadingPreview(false);
    }
  };

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex < files.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleClose = useCallback(() => {
    setTextContent("");
    setPreviewFile(null);
    setZoomLevel(1);
    setIsZoomExpanded(false);
    onClose();
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
      if (e.key === "ArrowLeft" && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
      if (e.key === "ArrowRight" && currentIndex < files.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose, currentIndex, files.length]);

  if (!isOpen || !previewFile) return null;

  // Fallback when nothing can be previewed - keeps the download path available.
  const renderUnsupportedFallback = () => (
    <div className="p-10 text-center">
      <div className="flex justify-center mb-4">
        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-800 my-3">{previewFile?.name}</h3>
      <p className="text-gray-600">Preview not available for this file type</p>
      <a
        href={previewFile?.url}
        download={previewFile?.name}
        className="inline-block mt-5 px-5 py-2.5 bg-[#0068FF] text-white no-underline rounded-md hover:bg-[#0056d2] transition-colors"
      >
        Download File
      </a>
    </div>
  );

  // Renders the HTML that loadFilePreview already produced locally with mammoth
  // (docx) / ExcelJS (xlsx, csv), for blob-backed files the external viewers
  // cannot fetch.
  const renderLocalHtml = () => {
    if (!textContent) {
      return isLoadingPreview ? null : renderUnsupportedFallback();
    }
    return (
      <div
        className="p-5 max-h-[70vh] overflow-auto w-full leading-relaxed transition-transform duration-200"
        style={{
          transform: `scale(${zoomLevel})`,
          transformOrigin: "top left",
        }}
      >
        <div
          dangerouslySetInnerHTML={{ __html: textContent }}
          className="font-sans leading-relaxed"
          style={{ overflowX: "auto", minWidth: "max-content" }}
        />
      </div>
    );
  };

  const renderPreviewContent = () => {
    if (!previewFile) return null;

    const { type, name, url } = previewFile;

    // Image files
    if (type?.startsWith("image/")) {
      return (
        <img
          src={url}
          alt={name}
          className="max-w-full max-h-[70vh] object-contain transition-transform duration-200"
          style={{ transform: `scale(${zoomLevel})` }}
          onLoad={() => setIsLoadingPreview(false)}
        />
      );
    }

    // DOCX files
    if (
      type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      name?.endsWith(".docx")
    ) {
      if (isLocalUrl(url)) {
        return renderLocalHtml();
      }
      return (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    previewFile.url,
                  )}`}
                  className="w-full h-full  border-0"
                  title={previewFile.name}
                />
        // <div
        //   className="p-5 max-h-[70vh] overflow-auto w-full leading-relaxed transition-transform duration-200"
        //   style={{
        //     transform: `scale(${zoomLevel})`,
        //     transformOrigin: "top left",
        //   }}
        // >
        //   <div
        //     dangerouslySetInnerHTML={{ __html: textContent }}
        //     className="font-sans leading-relaxed"
        //   />
        // </div>
      );
    }

    // Excel/CSV files
    if (
      type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      type === "application/vnd.ms-excel" ||
      type === "text/csv" ||
      name?.endsWith(".xlsx") ||
      name?.endsWith(".xls") ||
      name?.endsWith(".csv")
    ) {
      if (isLocalUrl(url)) {
        return renderLocalHtml();
      }
      return (
        <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    previewFile.url,
                  )}`}
                  className="w-full h-full  border-0"
                  title={previewFile.name}
                />
        // <div
        //   className="p-5 max-h-[70vh] w-full transition-transform duration-200"
        //   style={{
        //     transform: `scale(${zoomLevel})`,
        //     transformOrigin: "top left",
        //     overflow: "auto",
        //   }}
        // >
        //   <div 
        //     dangerouslySetInnerHTML={{ __html: textContent }}
        //     style={{
        //       overflowX: "auto",
        //       minWidth: "max-content",
        //     }}
        //   />
        // </div>
      );
    }

    // PDF files
    if (type === "application/pdf") {
      // Blob/data URLs are rendered by the browser's built-in PDF viewer.
      if (isLocalUrl(url)) {
        return (
          <div className="w-full h-full">
            <iframe
              src={url}
              title={name}
              className="w-full h-full min-h-[500px] border-0 bg-gray-100"
              onLoad={() => setIsLoadingPreview(false)}
            />
          </div>
        );
      }
      return (
    // <div className="w-full h-full flex flex-col" style={{ minHeight: '60vh' }}>
    //   <iframe
    //     src={`${url}#toolbar=1&navpanes=1&scrollbar=1`}
    //     className="w-full flex-1 border-none"
    //     style={{ minHeight: '60vh', height: '100%' }}
    //     title={name}
    //     onLoad={() => setIsLoadingPreview(false)}
    //   />
    // </div>
            <div className="w-full h-full">
          {/* <iframe
            src={previewFile.url}
            title={previewFile.name}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: '#f3f4f6',
            }}
          /> */}
          <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(
                    previewFile.url,
                  )}&embedded=true`}
                  className="w-full h-full min-h-[500px] border-0"
                  title={previewFile.name}
                />
        </div>
  );

      // return (
      //   <div className="p-5 w-full max-h-[70vh] overflow-auto flex flex-col items-start bg-gray-100">
      //     <div className="inline-block shadow-md bg-white">
      //       <canvas ref={canvasRef} className="block" />
      //     </div>

      //     {/* PDF Page Navigation */}
      //     {pdfNumPages > 1 && (
      //       <div className="mt-5 inline-flex items-center gap-1 p-1 bg-gray-300 rounded-lg shadow self-center">
      //         <button
      //           onClick={() => setPdfPageNum((prev) => Math.max(prev - 1, 1))}
      //           disabled={pdfPageNum <= 1}
      //           className={`flex items-center justify-center w-5 h-5 bg-transparent border-none rounded-md transition-all text-sm ${
      //             pdfPageNum <= 1 ? "text-gray-300 cursor-not-allowed" : "text-gray-600 cursor-pointer hover:bg-gray-200"
      //           }`}
      //           title="Previous page"
      //         >
      //           <FaChevronLeft />
      //         </button>

      //         <div className="flex items-center px-3 min-w-[65px] justify-center">
      //           <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
      //             {pdfPageNum} / {pdfNumPages}
      //           </span>
      //         </div>

      //         <button
      //           onClick={() => setPdfPageNum((prev) => Math.min(prev + 1, pdfNumPages))}
      //           disabled={pdfPageNum >= pdfNumPages}
      //           className={`flex items-center justify-center w-5 h-5 bg-transparent border-none rounded-md transition-all text-sm ${
      //             pdfPageNum >= pdfNumPages
      //               ? "text-gray-300 cursor-not-allowed"
      //               : "text-gray-600 cursor-pointer hover:bg-gray-200"
      //           }`}
      //           title="Next page"
      //         >
      //           <FaChevronRight />
      //         </button>
      //       </div>
      //     )}
      //   </div>
      // );
    }

    // Audio files
    if (type?.startsWith("audio/")) {
      return (
        <div className="p-10 text-center w-full flex flex-col items-center">
          <div className="w-16 h-16 mb-4 flex items-center justify-center bg-gray-100 rounded-full">
            <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
              />
            </svg>
          </div>
          <audio controls className="w-full mt-5">
            <source src={url} type={type} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    // Text files
    if (type === "text/plain") {
      return (
        <div className="p-5 max-h-[70vh] overflow-auto">
          <pre
            className="whitespace-pre-wrap font-mono transition-transform duration-200"
            style={{
              transform: `scale(${zoomLevel})`,
              transformOrigin: "top left",
            }}
          >
            {textContent}
          </pre>
        </div>
      );
    }

    // Default fallback for unsupported file types
    return renderUnsupportedFallback();
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/75 z-[2000000] flex items-center justify-center p-5 animate-fadeIn"
      onClick={handleClose}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-4 w-full z-[2000010]">
        <div className="flex items-center">
          {/* Close Button */}
          <button
            className="bg-transparent border-none cursor-pointer p-1 mr-4"
            onClick={handleClose}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* File Info */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center">
              {getFileIcon(previewFile.type || getFileTypeFromExtension(previewFile.name), "w-6 h-6")}
            </div>
            <h3 className="m-0 text-white text-base font-normal max-w-[400px] overflow-hidden text-ellipsis whitespace-nowrap">
              {previewFile.name}
            </h3>
          </div>
        </div>

        {/* Download Button */}
        {previewFile && !previewFile.type?.startsWith("audio/") && (
          <a target="_blank"
            rel="noreferrer"
            href={previewFile.url}
            download={previewFile.name}
            className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </a>
        )}
      </div>

      {/* Navigation Buttons */}
      {files.length > 1 && (
        <>
          <button
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg z-[2000010] transition-all hover:bg-gray-100 hover:scale-105 ${
              currentIndex === 0 ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
            }`}
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            className={`absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg z-[2000010] transition-all hover:bg-gray-100 hover:scale-105 ${
              currentIndex === files.length - 1 ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
            }`}
            onClick={handleNext}
            disabled={currentIndex === files.length - 1}
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Modal Content Container */}
      <div
        className="relative w-full max-w-5xl h-auto max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Content */}
        <div
          className={`bg-white rounded-xl w-full h-[80vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp ${
            previewFile.type?.startsWith("audio/") ? "justify-center max-h-[45vh]" : ""
          }`}
        >
          {/* Preview Content */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-5 bg-gray-50">
            {renderPreviewContent()}

            {/* Loading Overlay */}
            {isLoadingPreview && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                <Loader />
              </div>
            )}
          </div>

          {/* Zoom Controls */}
          {/* {previewFile && !previewFile.type?.startsWith("audio/") && (
            <div className="flex justify-center items-center p-4 border-t border-gray-200">
              <div
                className={`flex items-center gap-2 bg-white rounded-full shadow-md cursor-pointer transition-all ${
                  isZoomExpanded ? "px-4 py-2" : "p-3 w-12 h-12 justify-center"
                }`}
                onClick={() => !isZoomExpanded && setIsZoomExpanded(true)}
              >
                <div className="flex items-center text-lg text-gray-700">
                  <FaSearchPlus />
                </div>

                {isZoomExpanded && (
                  <>
                    <span className="text-sm font-semibold min-w-[45px] text-center text-gray-700 select-none">
                      {Math.round(zoomLevel * 100)}%
                    </span>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        zoomOut();
                      }}
                      disabled={zoomLevel <= 0.5}
                      className={`flex items-center justify-center w-8 h-8 text-lg bg-transparent border-none rounded-full font-bold transition-all ${
                        zoomLevel <= 0.5 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 cursor-pointer hover:bg-gray-100"
                      }`}
                      title="Zoom Out"
                    >
                      −
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        zoomIn();
                      }}
                      disabled={zoomLevel >= 3}
                      className={`flex items-center justify-center w-8 h-8 text-xl bg-transparent border-none rounded-full transition-all ${
                        zoomLevel >= 3 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 cursor-pointer hover:bg-gray-100"
                      }`}
                      title="Zoom In"
                    >
                      +
                    </button>
                  </>
                )}
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FilePreviewModal;
